import {Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import { IsdsMessageStateFilterDto } from "../../client/message-state/IsdsMessageStateApiClient";
import {MessageStateTranslKey} from "./MessageStateTranslKey";
import {IsdsMessageState} from "../../client/message/IsdsMessageApiClient";
import {CopyUtils} from "../../util/CopyUtils";
import {MessageTranslKey} from "../message/MessageTranslKey";
import {MessageStatusFormatter} from "../message/MessageStatusFormatter";
import SingleSelect from "../../common/form/SingleSelect";
import {DateTimeFormatter} from "../../common/DateTimeFormatter";
import DateField from "../../common/form/DateField";
import TextField from "../../common/form/TextField";

export interface MessageFilterFormProps {
    filter: IsdsMessageStateFilterDto;
    onChange: (filter: IsdsMessageStateFilterDto) => void;
}

export function MessageStateFilterForm(props: Readonly<MessageFilterFormProps>) {
    const {filter, onChange} = props;

    const {t} = useTranslation();

    function onMessageStateChange(value: unknown) {
        const stringValue = typeof value === "string" ? value.trim() : "";

        let nextState: IsdsMessageState | undefined;
        if (Object.values(IsdsMessageState).includes(stringValue as IsdsMessageState)) {
            nextState = stringValue as IsdsMessageState;
        } else {
            nextState = undefined;
        }

        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.stateType = nextState;
        onChange(updatedFilter);
    }

    const messageStatusOptions = [
        { value: "", label: t(MessageTranslKey.ALL_CURRENT_STATES)},
        ...Object.values(IsdsMessageState).map(state => ({
            value: state,
            label: MessageStatusFormatter.formatMessageStatusStatus(state, t),
        }))
    ];

    function onMessageCreatedSinceChange(changedSince: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.changedSince = DateTimeFormatter.convertDateToIsoFormat(changedSince);
        console.log(updatedFilter.changedSince);
        onChange(updatedFilter);
    }

    function onMessageCreateToChange(changedTo: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        const isoFormatChangedTo = DateTimeFormatter.convertDateToIsoFormat(changedTo);
        updatedFilter.changedTo = DateTimeFormatter.formatAsIsoDateTimeAtEndOfDay(isoFormatChangedTo);
        console.log(updatedFilter.changedTo);
        onChange(updatedFilter);
    }

    function onMessageStatusCodeChange(recipient: string) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.statusCode = normalizeString(recipient);
        onChange(updatedFilter);
    }

    function onMessageErrorCodeChange(recipient: string) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.errorCode = normalizeString(recipient);
        onChange(updatedFilter);
    }

    function normalizeString(value: string): string | undefined {
        const normalizedValue = value.trim();
        return (normalizedValue.length > 0) ? normalizedValue : undefined;
    }

    return (
        <Grid container mt={0} spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <SingleSelect label={t(MessageStateTranslKey.STATE)}
                              options={messageStatusOptions}
                              value={filter.stateType ?? ""}
                              onChange={onMessageStateChange} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <DateField label={t(MessageStateTranslKey.CREATE_SINCE)}
                           value={DateTimeFormatter.convertFromIsoDateTimeString(filter.changedSince)}
                           onChange={onMessageCreatedSinceChange}/>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <DateField label={t(MessageStateTranslKey.CREATE_TO)}
                           value={DateTimeFormatter.convertFromIsoDateTimeString(filter.changedTo)}
                           onChange={onMessageCreateToChange}/>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <TextField label={t(MessageStateTranslKey.STATUS_CODE)}
                           value={filter.statusCode}
                           onChange={onMessageStatusCodeChange} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <TextField label={t(MessageStateTranslKey.ERROR_CODE)}
                           value={filter.errorCode}
                           onChange={onMessageErrorCodeChange} />
            </Grid>
        </Grid>
    );
}
